/**
 * Member Table Component
 *
 * Displays organization members with expandable rows and inline role management.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { BundleWithStatus } from '@features/subscriptions';
import {
  formatPrice,
  getAddOnsIncludedInTier,
  getMonthlyAmountCents,
  useBundleCatalog,
  useBundlingEnabled,
  useGetFullSubscriptionDetailsQuery,
} from '@features/subscriptions';
import { ProviderBadge } from '@jotpsych/ui';
import {
  Edit as EditIcon,
  Email,
  ExpandLess,
  ExpandMore,
  Person,
  Phone,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';
import type { ProviderRoleInfo } from '@store/generated/organizations';
import { getRoleBadgeColor, getRoleDisplayName, ROLES } from '@utils/roleUtils';
import { useSnackbar } from 'notistack';

import { useGetMembershipInfoQuery, useReactivateMemberMutation, useRemoveMemberBundleMutation } from '../store/api';
import { useOperationLoading } from '../store/hooks';
import type { OrganizationMemberTableRow } from '../types';

import { ExpandableDataTable } from './ExpandableDataTable';
import { PersonCardModal } from './PersonCardModal';

interface MemberTableProps {
  title: string;
  members: OrganizationMemberTableRow[];
  expandedRows: Set<string>;
  onToggleExpand: (userId: string) => void;
  onMemberChange: () => void;
  availableRoles?: ProviderRoleInfo[];
  isInactive?: boolean;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  /** When set, auto-opens the edit dialog for this user ID and calls onAutoEditHandled */
  autoEditUserId?: string | null;
  /** Called after the auto-edit dialog is opened so parent can clear the ID */
  onAutoEditHandled?: () => void;
}

/**
 * Small inline component that fetches membership-info and shows a "Co-sign" chip
 * when needs_cosign is true. RTK Query caches each user's response.
 */
function CosignBadge({ userId }: { userId: string }) {
  const { data } = useGetMembershipInfoQuery({ userId });
  if (!data?.needs_cosign) return null;
  return (
    <Tooltip title="This provider's notes require co-signature from someone with the Supervisor role.">
      <Chip
        label="Co-sign"
        size="small"
        color="warning"
        variant="outlined"
        sx={{
          height: 24,
          fontWeight: 600,
          '& .MuiChip-label': { fontSize: '0.75rem' },
        }}
      />
    </Tooltip>
  );
}

export function MemberTable({
  title,
  members,
  expandedRows,
  onToggleExpand,
  onMemberChange,
  availableRoles = [],
  isInactive = false,
  collapsible,
  defaultCollapsed,
  onCollapsedChange,
  autoEditUserId,
  onAutoEditHandled,
}: MemberTableProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { clinicians } = useEventTracking();
  const { setOperationLoading, isOperationLoading } = useOperationLoading();
  const { isBundlingEnabled: showBundling } = useBundlingEnabled();

  const [editingMember, setEditingMember] = useState<OrganizationMemberTableRow | null>(null);

  // Auto-open edit dialog when autoEditUserId is set
  useEffect(() => {
    if (autoEditUserId && members.length > 0) {
      const member = members.find(m => m.userId === autoEditUserId);
      if (member) {
        setEditingMember(member);
        onAutoEditHandled?.();
      }
    }
  }, [autoEditUserId, members, onAutoEditHandled]);

  // Mutations
  const [reactivateMember] = useReactivateMemberMutation();
  const [removeMemberBundle] = useRemoveMemberBundleMutation();

  // Bundle catalog for tier/add-on lookups (used for Cost column + inactive add-on chips)
  const { tiers: catalogTiers, addOns: availableAddOns } = useBundleCatalog();

  // Current billing interval drives per-member monthly cost (annual orgs show yearly/12)
  const { data: subscriptionDetails } = useGetFullSubscriptionDetailsQuery();
  const isAnnualBilling = subscriptionDetails?.billing_interval === 'year';

  // Compute monthly per-member cost for the active-members Cost column. Tier price + paid
  // add-ons (i.e. add-ons not already covered by the tier's grants via getAddOnsIncludedInTier).
  const computeMemberMonthlyCost = useCallback(
    (member: OrganizationMemberTableRow): { cents: number; currency: string } | null => {
      const tierAssignment = member.bundles?.find(
        b => b.bundle_type === 'tier' && b.status === 'active',
      );
      if (!tierAssignment) return null;

      const tierBundle = catalogTiers.find(t => t.bundle_id === tierAssignment.bundle_id);
      if (!tierBundle) return null;

      const memberAddOnBundles: BundleWithStatus[] = (member.bundles || [])
        .filter(b => b.bundle_type === 'add_on' && b.status === 'active')
        .map(b => availableAddOns.find(a => a.bundle_id === b.bundle_id))
        .filter((a): a is BundleWithStatus => a != null);

      const includedIds = new Set(
        getAddOnsIncludedInTier(tierBundle, memberAddOnBundles).map(a => a.bundle_id),
      );
      const paidAddOns = memberAddOnBundles.filter(a => !includedIds.has(a.bundle_id));

      const tierCents = getMonthlyAmountCents(tierBundle, isAnnualBilling);
      const addOnsCents = paidAddOns.reduce(
        (sum, a) => sum + getMonthlyAmountCents(a, isAnnualBilling),
        0,
      );

      return { cents: tierCents + addOnsCents, currency: tierBundle.currency };
    },
    [catalogTiers, availableAddOns, isAnnualBilling],
  );

  // Handle reactivation (for inactive members)
  const handleReactivateUser = useCallback(
    async (userId: string) => {
      const operationKey = `reactivate-${userId}`;
      setOperationLoading(operationKey, true);

      try {
        await reactivateMember({ userId }).unwrap();
        clinicians.memberReactivated({ member_id: userId });
        enqueueSnackbar('Member reactivated successfully', { variant: 'success' });
        onMemberChange();
      } catch (error: any) {
        enqueueSnackbar(error?.data?.message || 'Failed to reactivate member', {
          variant: 'error',
        });
      } finally {
        setOperationLoading(operationKey, false);
      }
    },
    [reactivateMember, enqueueSnackbar, onMemberChange, setOperationLoading, clinicians],
  );

  // Handle add-on removal
  const handleRemoveAddOn = useCallback(
    async (userId: string, assignmentId: string) => {
      const operationKey = `remove-addon-${userId}-${assignmentId}`;
      setOperationLoading(operationKey, true);

      try {
        await removeMemberBundle({ userId, assignmentId }).unwrap();
        enqueueSnackbar('Add-on removed successfully', { variant: 'success' });
        onMemberChange();
      } catch (error: any) {
        enqueueSnackbar(error?.data?.message || 'Failed to remove add-on', { variant: 'error' });
      } finally {
        setOperationLoading(operationKey, false);
      }
    },
    [removeMemberBundle, enqueueSnackbar, onMemberChange, setOperationLoading],
  );

  // Render expanded content
  const renderExpandedContent = useCallback((member: OrganizationMemberTableRow) => {
    const billing = member.billingInfo;
    const hasBillingInfo = billing && (billing.license_type || billing.npi_number || billing.facility_address);

    return (
      <Box>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: hasBillingInfo ? 4 : 6 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
              <Person sx={{ fontSize: 16, mr: 0.5 }} />
              User Information
            </Typography>
            <Box component="dl" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography component="dt" variant="body2" sx={{ width: 96, color: 'text.secondary' }}>Name:</Typography>
                <Typography component="dd" variant="body2">{member.name || 'Not provided'}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography component="dt" variant="body2" sx={{ width: 96, color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
                  <Email sx={{ fontSize: 12, mr: 0.5 }} />
                  Email:
                </Typography>
                <Typography component="dd" variant="body2">{member.email || 'Not provided'}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography component="dt" variant="body2" sx={{ width: 96, color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
                  <Phone sx={{ fontSize: 12, mr: 0.5 }} />
                  Phone:
                </Typography>
                <Typography component="dd" variant="body2">{member.phoneNumber || 'Not provided'}</Typography>
              </Box>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: hasBillingInfo ? 4 : 6 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>Metrics & Details</Typography>
            <Box component="dl" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography component="dt" variant="body2" sx={{ width: 128, color: 'text.secondary' }}>User ID:</Typography>
                <Typography component="dd" variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{member.userId}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography component="dt" variant="body2" sx={{ width: 128, color: 'text.secondary' }}>Notes Created:</Typography>
                <Typography component="dd" variant="body2">{member.notesCreated || 0}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography component="dt" variant="body2" sx={{ width: 128, color: 'text.secondary' }}>Status:</Typography>
                <Typography component="dd" variant="body2">
                  {member.status === 'active' ? (
                    <Chip
                      label="Active"
                      size="small"
                      sx={{
                        height: 24,
                        fontWeight: 600,
                        '& .MuiChip-label': { fontSize: '0.75rem' },
                        backgroundColor: theme => alpha(theme.palette.success.main, 0.1),
                        color: theme => theme.palette.success.dark,
                        border: '1px solid',
                        borderColor: theme => theme.palette.success.dark,
                      }}
                    />
                  ) : (
                    <Chip
                      label="Inactive"
                      size="small"
                      sx={{
                        height: 24,
                        fontWeight: 600,
                        '& .MuiChip-label': { fontSize: '0.75rem' },
                        backgroundColor: theme => alpha(theme.palette.grey[500], 0.1),
                        color: theme => theme.palette.grey[700],
                        border: '1px solid',
                        borderColor: theme => theme.palette.grey[500],
                      }}
                    />
                  )}
                </Typography>
              </Box>
              {member.deactivatedAt && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography component="dt" variant="body2" sx={{ width: 128, color: 'text.secondary' }}>Deactivated:</Typography>
                  <Typography component="dd" variant="body2">
                    {new Date(member.deactivatedAt).toLocaleDateString()}
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>

          {/* Billing Information (if available) */}
          {hasBillingInfo && (
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>Billing Information</Typography>
              <Box component="dl" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {billing.license_type && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography component="dt" variant="body2" sx={{ width: 96, color: 'text.secondary' }}>License:</Typography>
                    <Typography component="dd" variant="body2">
                      {billing.license_type}
                      {billing.license_number && ` #${billing.license_number}`}
                    </Typography>
                  </Box>
                )}
                {billing.npi_number && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography component="dt" variant="body2" sx={{ width: 96, color: 'text.secondary' }}>NPI:</Typography>
                    <Typography component="dd" variant="body2">{billing.npi_number}</Typography>
                  </Box>
                )}
                {billing.facility_address && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Typography component="dt" variant="body2" sx={{ width: 96, color: 'text.secondary' }}>Address:</Typography>
                    <Typography component="dd" variant="body2">
                      {billing.facility_address}
                      {billing.city && billing.state && `, ${billing.city}, ${billing.state}`}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  }, [isInactive]);

  // Column definitions
  const columns = useMemo(() => {
    if (isInactive) {
      // Inactive members columns
      return [
        {
          key: 'expand' as any,
          label: '',
          render: (_: unknown, member: OrganizationMemberTableRow) => (
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation();
                onToggleExpand(member.userId);
              }}
            >
              {expandedRows.has(member.userId) ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          ),
        },
        {
          key: 'name' as any,
          label: 'Name',
          render: (value: unknown, member: OrganizationMemberTableRow) => {
            const name = value == null ? null : String(value);
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Outlined ProviderBadge conveys "deactivated" — visual rule:
                    filled = active, outlined = inactive. */}
                <ProviderBadge name={name} size="compact" selected={false} />
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {name ?? 'Unknown'}
                </Typography>
                {member.isHistorical && (
                  <Tooltip title="Historical member: exists so migrated records attribute to this clinician. Cannot log in or be reactivated.">
                    <Chip variant="soft" size="xSmall" label="Historical" />
                  </Tooltip>
                )}
              </Box>
            );
          },
        },
        {
          key: 'roles' as any,
          label: 'Previous Roles',
          render: (_: unknown, member: OrganizationMemberTableRow) => {
            return (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                {member.roles.map(role => {
                  const badgeColor = getRoleBadgeColor(role);
                  return (
                    <Chip
                      key={role}
                      label={getRoleDisplayName(role)}
                      size="small"
                      sx={{
                        height: 24,
                        fontWeight: 600,
                        opacity: 0.6,
                        '& .MuiChip-label': { fontSize: '0.75rem' },
                        backgroundColor: theme => {
                          if (badgeColor === 'default') return alpha(theme.palette.grey[500], 0.1);
                          return alpha(theme.palette[badgeColor].main, 0.1);
                        },
                        color: theme => {
                          if (badgeColor === 'default') return theme.palette.grey[700];
                          return theme.palette[badgeColor].dark;
                        },
                        border: '1px solid',
                        borderColor: theme => {
                          if (badgeColor === 'default') return theme.palette.grey[500];
                          return theme.palette[badgeColor].dark;
                        },
                      }}
                    />
                  );
                })}
                {member.roles.length === 0 && (
                  <Typography variant="body2" color="text.disabled">
                    No previous roles
                  </Typography>
                )}
              </Box>
            );
          },
        },
        // Conditionally add bundle columns when feature is enabled
        ...(showBundling
          ? [
              {
                key: 'bundles' as any,
                label: 'Tier',
                render: (_: unknown, member: OrganizationMemberTableRow) => {
                  const tier = member.bundles?.find(b => b.bundle_type === 'tier');
                  if (!tier) {
                    return (
                      <Typography variant="body2" color="text.disabled">
                        —
                      </Typography>
                    );
                  }
                  return (
                    <Chip
                      label={tier.bundle_name}
                      size="small"
                      color="default"
                      variant="outlined"
                      sx={{ opacity: 0.6 }}
                    />
                  );
                },
              },
              {
                key: 'addons' as any,
                label: 'Add-ons',
                render: (_: unknown, member: OrganizationMemberTableRow) => {
                  const addOns = member.bundles?.filter(b => b.bundle_type === 'add_on') || [];
                  if (addOns.length === 0) {
                    return (
                      <Typography variant="body2" color="text.disabled">
                        —
                      </Typography>
                    );
                  }
                  return (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {addOns.map(addon => (
                        <Chip
                          key={addon.bundle_id}
                          label={addon.bundle_name}
                          size="small"
                          variant="outlined"
                          sx={{ opacity: 0.6 }}
                        />
                      ))}
                    </Box>
                  );
                },
              },
            ]
          : []),
        {
          key: 'userId' as any,
          label: 'Actions',
          align: 'right' as const,
          render: (_: unknown, member: OrganizationMemberTableRow) => {
            if (member.isHistorical) {
              // Historical members are permanently inactive (the server refuses
              // reactivation with 409) — offer nothing rather than a dead button.
              return (
                <Typography variant="body2" color="text.disabled">
                  —
                </Typography>
              );
            }
            const isLoading = isOperationLoading(`reactivate-${member.userId}`);
            return (
              <Button
                size="small"
                variant="outlined"
                color="primary"
                disabled={isLoading}
                onClick={e => {
                  e.stopPropagation();
                  handleReactivateUser(member.userId);
                }}
                startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {isLoading ? 'Reactivating...' : 'Reactivate'}
              </Button>
            );
          },
        },
      ];
    }

    // Active members columns — no expand button; entire row opens the edit dialog.
    return [
      {
        key: 'name' as any,
        label: 'Name',
        render: (value: unknown) => {
          const name = value == null ? null : String(value);
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ProviderBadge name={name} size="compact" />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {name ?? 'Unknown'}
              </Typography>
            </Box>
          );
        },
      },
      {
        key: 'roles' as any,
        label: 'Roles',
        render: (_: unknown, member: OrganizationMemberTableRow) => {
          return (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
              {member.roles.map(role => {
                const badgeColor = getRoleBadgeColor(role);
                return (
                  <Chip
                    key={role}
                    label={getRoleDisplayName(role)}
                    size="small"
                    sx={{
                      height: 24,
                      fontWeight: 600,
                      '& .MuiChip-label': { fontSize: '0.75rem' },
                      backgroundColor: theme => {
                        if (badgeColor === 'default') return alpha(theme.palette.grey[500], 0.1);
                        return alpha(theme.palette[badgeColor].main, 0.1);
                      },
                      color: theme => {
                        if (badgeColor === 'default') return theme.palette.grey[700];
                        return theme.palette[badgeColor].dark;
                      },
                      border: '1px solid',
                      borderColor: theme => {
                        if (badgeColor === 'default') return theme.palette.grey[500];
                        return theme.palette[badgeColor].dark;
                      },
                    }}
                  />
                );
              })}
              {member.roles.includes(ROLES.CLINICIAN) && (
                <CosignBadge userId={member.userId} />
              )}
            </Box>
          );
        },
      },
      // Conditionally add bundle columns when feature is enabled
      ...(showBundling
        ? [
            {
              key: 'bundles' as any,
              label: 'Tier',
              render: (_: unknown, member: OrganizationMemberTableRow) => {
                const isClinician = member.roles.includes(ROLES.CLINICIAN);
                const tier = member.bundles?.find(
                  b => b.bundle_type === 'tier' && b.status === 'active',
                );

                // Only clinicians can have bundles
                if (!isClinician) {
                  return (
                    <Typography variant="body2" color="text.secondary">
                      —
                    </Typography>
                  );
                }

                // Tier-less clinicians during trial: surface "Trialing" instead of "No tier"
                if (!tier && subscriptionDetails?.is_trialing) {
                  return (
                    <Chip
                      label="Trialing"
                      size="small"
                      color="warning"
                      variant="outlined"
                      sx={{
                        height: 24,
                        fontWeight: 600,
                        '& .MuiChip-label': { fontSize: '0.75rem' },
                      }}
                    />
                  );
                }

                return (
                  <Chip
                    label={tier?.bundle_name || 'No tier'}
                    size="small"
                    color={tier ? 'primary' : 'default'}
                    variant="outlined"
                    sx={{
                      height: 24,
                      fontWeight: 600,
                      '& .MuiChip-label': { fontSize: '0.75rem' },
                    }}
                  />
                );
              },
            },
            {
              key: 'addons' as any,
              label: 'Add-ons',
              render: (_: unknown, member: OrganizationMemberTableRow) => {
                const isClinician = member.roles.includes(ROLES.CLINICIAN);
                const addOns =
                  member.bundles?.filter(
                    b => b.bundle_type === 'add_on' && b.status === 'active',
                  ) || [];

                // Only clinicians can have bundles
                if (!isClinician) {
                  return (
                    <Typography variant="body2" color="text.secondary">
                      —
                    </Typography>
                  );
                }

                return (
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    {addOns.map(addon => (
                      <Chip
                        key={addon.bundle_id}
                        label={addon.bundle_name}
                        size="small"
                        variant="outlined"
                        sx={{
                          height: 24,
                          fontWeight: 600,
                          '& .MuiChip-label': { fontSize: '0.75rem' },
                        }}
                      />
                    ))}
                    {addOns.length === 0 && (
                      <Typography variant="body2" color="text.disabled">
                        —
                      </Typography>
                    )}
                  </Box>
                );
              },
            },
            {
              key: 'cost' as any,
              label: 'Cost',
              align: 'right' as const,
              render: (_: unknown, member: OrganizationMemberTableRow) => {
                const isClinician = member.roles.includes(ROLES.CLINICIAN);
                if (!isClinician) {
                  return (
                    <Typography variant="body2" color="text.secondary">
                      $0/mo
                    </Typography>
                  );
                }

                const cost = computeMemberMonthlyCost(member);
                if (!cost) {
                  return (
                    <Typography variant="body2" color="text.disabled">
                      —
                    </Typography>
                  );
                }

                return (
                  <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                    {formatPrice(cost.cents, cost.currency)}
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ color: 'text.secondary', ml: 0.5 }}
                    >
                      /mo
                    </Typography>
                  </Typography>
                );
              },
            },
          ]
        : []),
      {
        key: 'actions' as any,
        label: 'Actions',
        align: 'right' as const,
        render: (_: unknown, member: OrganizationMemberTableRow) => {
          if (isInactive) {
            const isLoading = isOperationLoading(`reactivate-${member.userId}`);
            return (
              <Button
                size="small"
                variant="outlined"
                color="primary"
                disabled={isLoading}
                onClick={e => {
                  e.stopPropagation();
                  handleReactivateUser(member.userId);
                }}
                startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {isLoading ? 'Reactivating...' : 'Reactivate'}
              </Button>
            );
          }
          return (
            <Tooltip title="Edit Member">
              <span>
                <IconButton
                  size="small"
                  onClick={e => {
                    e.stopPropagation();
                    clinicians.memberEditOpened({
                      member_id: member.userId,
                      role_count: member.roles.length,
                      has_tier: !!member.bundles?.find(
                        b => b.bundle_type === 'tier' && b.status === 'active',
                      ),
                      addon_count:
                        member.bundles?.filter(
                          b => b.bundle_type === 'add_on' && b.status === 'active',
                        ).length || 0,
                    });
                    setEditingMember(member);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          );
        },
      },
    ];
  }, [
    isInactive,
    expandedRows,
    onToggleExpand,
    isOperationLoading,
    handleReactivateUser,
    showBundling,
    computeMemberMonthlyCost,
    subscriptionDetails?.is_trialing,
    clinicians,
  ]);

  // Active rows open the edit dialog on click; inactive rows remain expand-only.
  const handleRowClick = useCallback(
    (member: OrganizationMemberTableRow) => {
      clinicians.memberEditOpened({
        member_id: member.userId,
        role_count: member.roles.length,
        has_tier: !!member.bundles?.find(
          b => b.bundle_type === 'tier' && b.status === 'active',
        ),
        addon_count:
          member.bundles?.filter(
            b => b.bundle_type === 'add_on' && b.status === 'active',
          ).length || 0,
      });
      setEditingMember(member);
    },
    [clinicians],
  );

  return (
    <>
      <ExpandableDataTable
        title={title}
        columns={columns}
        data={members}
        total={members.length}
        loading={false}
        emptyMessage={isInactive ? 'No inactive members' : 'No active members'}
        expandedRows={expandedRows}
        getRowId={(member: OrganizationMemberTableRow) => member.userId}
        renderExpandedContent={renderExpandedContent as any}
        onRowClick={isInactive ? undefined : handleRowClick}
        collapsible={collapsible}
        defaultCollapsed={defaultCollapsed}
        onCollapsedChange={onCollapsedChange}
      />

      {/* Person Card Modal (replaces EditMemberDialog) */}
      {!isInactive && (
        <PersonCardModal
          open={editingMember !== null}
          onClose={() => setEditingMember(null)}
          onSuccess={() => {
            setEditingMember(null);
            onMemberChange();
          }}
          member={editingMember}
          availableRoles={availableRoles}
        />
      )}
    </>
  );
}
